import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Tile,
  Tag,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  InlineNotification,
} from '@carbon/react';
import { Renew, Copy } from '@carbon/icons-react';
import { listDocuments, getPublicUrl } from '@/lib/documentsApi';

const headers = [
  { key: 'type', header: 'Type' },
  { key: 'client', header: 'Client' },
  { key: 'status', header: 'Status' },
  { key: 'date', header: 'Created' },
  { key: 'actions', header: 'Link' },
];

const statusColor = {
  draft: 'gray',
  sent: 'blue',
  signed: 'green',
  pending_payment: 'purple',
  paid: 'green',
};

const DocumentTracker = ({ refreshKey }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const docs = await listDocuments(user.id);
      setDocuments(docs);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const copyLink = (doc) => {
    const url = getPublicUrl(doc.type, doc.publicToken || doc.id);
    navigator.clipboard.writeText(url);
    setNotification({ kind: 'success', title: 'Link copied', subtitle: url });
    setTimeout(() => setNotification(null), 2500);
  };

  const rows = documents.map((doc) => ({
    id: doc.id,
    type: doc.type,
    client: doc.clientName || doc.clientEmail || '—',
    status: doc.status,
    date: doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—',
    actions: doc.publicToken || doc.id,
  }));

  return (
    <Tile className="document-tracker">
      <div className="tracker-header">
        <h3>Document Tracker</h3>
        <Button kind="ghost" size="sm" renderIcon={Renew} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </div>

      {notification && (
        <InlineNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.subtitle}
          lowContrast
          onClose={() => setNotification(null)}
        />
      )}

      {rows.length === 0 ? (
        <p className="tracker-empty">No documents yet. Create a contract, invoice, or timesheet below.</p>
      ) : (
        <DataTable rows={rows} headers={headers} size="sm">
          {({ rows: tableRows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
            <Table {...getTableProps()} size="sm">
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>
                        {cell.info.header === 'type' && (
                          <span className="tracker-type">{cell.value}</span>
                        )}
                        {cell.info.header === 'status' && (
                          <Tag type={statusColor[cell.value] || 'gray'} size="sm">
                            {cell.value}
                          </Tag>
                        )}
                        {cell.info.header === 'actions' && (
                          <Button kind="ghost" size="sm" renderIcon={Copy} onClick={() => copyLink(documents.find((d) => d.id === row.id))}>
                            Copy link
                          </Button>
                        )}
                        {!['type', 'status', 'actions'].includes(cell.info.header) && cell.value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DataTable>
      )}
    </Tile>
  );
};

export default DocumentTracker;
